import { PRO_PAGE_ROUTE_PER_PRO_TYPE } from '@application/constants';
import { translatedFieldWithFallback, useRouter } from '@application/utils';
import type {
  ProfessionalTypeEnum,
  ProsListGetProMapMarkerQuery,
  ProsListMapGetProQuery,
} from '@gql/graphql';
import { SearchOption } from '@presentation/components';
import { PLACEHOLDER_IMAGE } from '@presentation/constants';
import type { MapMakerType } from '@presentation/pages/ProList/components/Map/types';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';

export interface UseFormatMapMarkerParams {
  proType: ProfessionalTypeEnum;
  onProClick: (proId?: number) => void;
}

export const useFormatMapMarker = (params: UseFormatMapMarkerParams) => {
  const { proType, onProClick } = params;

  const { translate, infoFromCurrentPath, routeLocationDescriptor, language } = useRouter();

  const formatPopup = useCallback(
    (pro: ProsListGetProMapMarkerQuery['searchProfessionals']['professionals'][number]) => {
      const url = translatedFieldWithFallback(language, pro?.translations, 'url');
      const link = routeLocationDescriptor(PRO_PAGE_ROUTE_PER_PRO_TYPE[proType], {
        params: [url ?? ''],
        state: {
          returnSearch: infoFromCurrentPath?.search,
          returnParams: infoFromCurrentPath?.params,
        },
      });

      return (
        <SearchOption
          title={translatedFieldWithFallback(language, pro.translations, 'title') ?? ''}
          subtitle={pro.address?.formatted ?? ''}
          image={pro.imgs[0]?.medium ?? PLACEHOLDER_IMAGE}
        >
          <Link to={link}>
            {pro.onlineBookingEnabled
              ? translate('common.buttons.book')
              : translate('common.buttons.contactPro')}
            {' >'}
          </Link>
        </SearchOption>
      );
    },
    [
      infoFromCurrentPath?.params,
      infoFromCurrentPath?.search,
      language,
      proType,
      routeLocationDescriptor,
      translate,
    ],
  );

  const formatMarker = useCallback(
    (pro: ProsListMapGetProQuery['searchProfessionals']['professionals'][number]) => {
      let markerType: MapMakerType = 'point-grey';
      if (pro.onlineBookingEnabled && !pro.unclaimedProfile) markerType = 'marker-blue';
      if (!pro.onlineBookingEnabled && !pro.unclaimedProfile) markerType = 'marker-lightBlue';

      return {
        type: markerType,
        onClick: () => {
          onProClick(pro?.id);
        },
        position: {
          lat: pro.address!.lat!,
          lng: pro.address!.lng!,
        },
      };
    },
    [onProClick],
  );

  return { formatMarker, formatPopup };
};
